import React, { useEffect, useState, useRef } from "react";
import CommonModal from "../../../Settings/components/ResetContext/components/DumpModal";
import ImageWithFallback from "../../../../components/ImageFallback";
import Stepper from "./stepper";
import env from "../../../../config/env"
import { useUserInfo } from "../../../../context/user";
import Nango from "@nangohq/frontend";
import toast from "react-hot-toast";
import {
  useAvailableIntegrations,
  useCreateNangoConnectSession,
  useStoreNangoSession,
} from "../../../../queries/integrations";

const IntegrationModal = ({
  isOpen,
  onClose,
  requiredIntegrations,
  onAllConnected,
}) => {
  const userActiveLocation = useUserInfo()[4];
  const user = useUserInfo()[0];
  const [loadingProvider, setLoadingProvider] = useState(null);

  const NANGO_API_HOST = env.NANGO_API_HOST || "https://api.nango.dev";
  const NANGO_CONNECT_UI_HOST = env.NANGO_CONNECT_UI_HOST || "https://connect.nango.dev";
  const hasConnectedRef = useRef(false);

  const nango = new Nango({
    host: NANGO_API_HOST,
    apiURL: NANGO_API_HOST
  });

  const createSessionMutation = useCreateNangoConnectSession();
  const storeSessionMutation = useStoreNangoSession();

  // Use useRef to persist the interval reference across re-renders
  const connectionIntervalRef = useRef(null);
  const connectionTimeoutRef = useRef(null);

  console.log('userActiveLocation', userActiveLocation);

  const {
    data: availableData,
    isLoading: waiting,
    refetch: fetchIntegrations,
  } = useAvailableIntegrations(userActiveLocation?.id, user?.userId, undefined, {
    enabled: isOpen,
  });
  const integrations = availableData?.integrations ?? [];
  const connectedInt = availableData?.connected ?? [];

  useEffect(() => {
    if (!isOpen) {
      // Reset loading state when modal is closed
      setLoadingProvider(null);
      if (connectionIntervalRef.current) {
        clearInterval(connectionIntervalRef.current);
        connectionIntervalRef.current = null;
      }
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
    }
  }, []);

  const clearConnectionChecking = () => {
    if (connectionIntervalRef.current) {
      clearInterval(connectionIntervalRef.current);
      connectionIntervalRef.current = null;
    }
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    setLoadingProvider(null);
  };

  const handleClose = () => {
    // Reset loading state and clear interval when closing manually
    clearConnectionChecking();
    onClose();
  };

  const launchOAuthPopup = async (provider) => {
    setLoadingProvider(provider);
    try {
      const sessionResponse = await createSessionMutation.mutateAsync({
        userInfo: user,
        allowedIntegrations: [provider],
      });

      if (!sessionResponse?.success || !sessionResponse?.data?.token) {
        toast.error(sessionResponse?.message ||
          sessionResponse?.error ||
          "Failed to get session token")
        onClose()
        throw new Error(
          sessionResponse?.message ||
          sessionResponse?.error ||
          "Failed to get session token"
        );
      }

      const sessionToken = sessionResponse.data.token;
      const connect = nango.openConnectUI({
        baseURL: NANGO_CONNECT_UI_HOST,
        apiURL: NANGO_API_HOST,
        onEvent: async (event) => {
          if (event.type === "close") {
          } else if (event.type === "connect") {
            try {
              const { connectionId } = event.payload;
              const sendConnectionToBackend = await storeSessionMutation.mutateAsync({
                provider,
                connection_id: connectionId,
                profile_id: userActiveLocation?.id,
                user_id: user?.userId,
                agency_id: userActiveLocation?.agency_id,
              });
              if (sendConnectionToBackend?.success) {
                hasConnectedRef.current = true;
                fetchIntegrations();
              }
              else {
              }
            } catch (err) {
            }
          }
          setLoadingProvider(null);
        },
      });

      connect.setSessionToken(sessionToken);
    } catch (err) {
      onClose()
      toast.error(err?.response?.data?.message || "Failed to connect provider");
    }
  };


  const getIntegration = (provider) =>
    integrations.find((int) => int.provider === provider);

  const getDisplayName = (provider) =>
    getIntegration(provider)?.display_name || provider;

  const INSTALL_URLS = {
    monday: env.REACT_APP_MONDAY_URL,
    "microsoft-teams": env.REACT_APP_TEAMS_URL,
  };

  
  const providerDomain = (provider) => {
    const key = provider?.replaceAll(" ", "")?.toLowerCase() || "";

    const overrides = {
      fireflies: "fireflies.ai",
      roller: "mysupport.roller.software",
      facebook: "meta.com",
      "google-ads": "ads.google.com",
      "google-calendar": "calendar.google.com",
      "google-analytics": "analytics.google.com",
      "microsoft-teams": "teams.live.com",
    };

    if (key.includes("fathom")) return "fathom.ai";
    if (overrides[key]) return overrides[key];

    return `${key.split("-")[0]}.com`;
  };

  const isConnected = (provider) =>
    connectedInt.some((int) => int.provider === provider);

  const connectedCount = requiredIntegrations.filter(isConnected).length;

  useEffect(() => {
    if (
      isOpen &&
      connectedCount === requiredIntegrations.length &&
      requiredIntegrations.length > 0
    ) {
      console.log("All integrations connected, launching agent...");
      onAllConnected?.();
      handleClose();
    }
  }, [isOpen, connectedCount, requiredIntegrations.length, onAllConnected]);

  return (
    <CommonModal
      title=""
      isOpen={isOpen}
      onClose={handleClose}
      showAlert={false}
    >
      <div className="px-2 relative !max-h-[85vh] overflow-y-auto">
        {waiting && (
          <div className="absolute inset-0 bg-card bg-opacity-90 rounded-[15px] flex flex-col items-center justify-center z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mb-3"></div>
          </div>
        )}

        <h1 className="text-center text-md font-medium">
          Your AI Agent is almost ready. Just one last step.
        </h1>
        <p className="text-center text-sm mt-2">
          To activate your AI Agent, please connect all required integrations.
        </p>

        <div className="mt-5">
          {requiredIntegrations.map((provider, i) => {
            const connected = isConnected(provider);
            const displayName = getDisplayName(provider);

            return (
              <div
                key={i}
                className="flex items-center justify-between mt-3 border py-[7px] px-2.5 !border-border rounded-md"
              >
                <div className="flex items-center gap-4">
                  <ImageWithFallback
                    src={`https://www.google.com/s2/favicons?sz=64&domain_url=${providerDomain(
                      provider,
                    )}`}
                    alt={displayName}
                  />
                  <p className="text-sm font-medium">{displayName}</p>
                </div>
                {connected ? (
                  <span className="text-green-600 text-sm font-semibold border !border-green-600 rounded-md px-6 py-2">
                    Connected
                  </span>
                ) : provider === "monday" || provider === "microsoft-teams" ? (
                  <div className="flex items-center gap-2">
                    {getIntegration(provider)?.show_install_button && (
                      <button
                        onClick={() =>
                          INSTALL_URLS[provider] &&
                          window.open(INSTALL_URLS[provider], "_blank")
                        }
                        className="px-4 py-2 text-xs bg-white border flex flex-col items-center justify-center !border-black h-[42px] text-nowrap rounded text-black font-bold"
                      >
                   
                        INSTALL APP
                      </button>
                    )}
                    <button
                      className="px-10 py-2 text-xs bg-[#1E1E1E] flex flex-col items-center justify-center h-[42px] rounded text-white font-bold"
                      onClick={() => launchOAuthPopup(provider)}
                      disabled={loadingProvider === provider}
                    >
                
                      {loadingProvider === provider
                        ? `Connecting to ${displayName}...`
                        : `CONNECT`}
                    </button>
                  </div>
                ) : (
                  <button
                    className="bg-primary text-xs rounded-lg text-primary-foreground px-10 py-3 text-md"
                    onClick={() => launchOAuthPopup(provider)}
                    disabled={loadingProvider === provider}
                  >
                    {loadingProvider === provider
                      ? `Connecting to ${displayName}...`
                      : `Connect`}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex w-full my-6 items-center justify-center gap-5">
          <p className="text-[10px] text-muted-foreground my-3 text-nowrap">
            {connectedCount}/{requiredIntegrations.length} integrations
            connected
          </p>
          <Stepper
            stepsCount={requiredIntegrations.length}
            currentStep={connectedCount}
          />
        </div>

        <button
          className={`w-full py-3 rounded-md text-center font-medium text-lg ${connectedCount === requiredIntegrations.length
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-muted-foreground"
            }`}
          disabled={connectedCount !== requiredIntegrations.length}
          onClick={() => {
            if (connectedCount === requiredIntegrations.length) {
              onAllConnected?.();
              handleClose();
            }
          }}
        >
          Finish Agent Set Up
        </button>
      </div>
    </CommonModal>
  );
};

export default IntegrationModal;
